import { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";

import "../../css/Panel.css";
import "../../css/Patient.css";
import "../../css/PatientSearch.css";
import "../../css/custom-datatable.css";
import "../css/LaborControl.css";

import Dropdown from "../../components/Dropdown";
import InputIconSearchPanelMultiSelection from "../../components/InputIconSearchPanelMultiSelection";
import ButtonIconH from "../../components/ButtonIconH";
import Tag from "../../components/Tag";
import CheckBox from "../../components/CheckBox";
import TooltipBox from "../../components/Tooltip";
import PopupHoursEdition from "../components/PopupHoursEdition";
import PopupRegisterCancellation from "../components/PopupRegisterCancellation";
import PopupRegisterDeclaration from "../components/PopupRegisterDeclaration";
import { post } from "../../js/post";
import { userData } from "../../js/utils";
import {
  MONTHS,
  LABOR_CONTROL_TYPES,
  LABOR_CONTROL_STATUS,
} from "../js/constants";

function getMonthName(monthNumber) {
  return MONTHS.find((m) => m.id === monthNumber)?.text || "";
}

function parseDateParts(dateStr) {
  const [, month, day] = dateStr.split("-").map(Number);
  return { day, month };
}

function getRecordDate(row) {
  return row.tipoRegistro === "Diario" ? row.inicio : row.fecha;
}

function formatFechaColumn(row) {
  if (row.tipoRegistro === "Horario") {
    const { day, month } = parseDateParts(row.fecha);
    return `${day} DE ${getMonthName(month)}`;
  }

  if (row.tipoRegistro === "Diario") {
    const start = parseDateParts(row.inicio);
    const end = parseDateParts(row.fin);

    if (row.inicio === row.fin) {
      return `${start.day} DE ${getMonthName(start.month)}`;
    }

    if (start.month === end.month) {
      return `${start.day} - ${end.day} DE ${getMonthName(start.month)}`;
    }

    return `${start.day} DE ${getMonthName(start.month)} - ${end.day} DE ${getMonthName(end.month)}`;
  }

  return "";
}

function formatTime(timeStr) {
  return timeStr ? timeStr.slice(0, 5) : "";
}

function formatTimeColumn(row, field, correctedField) {
  if (row.tipoRegistro !== "Horario") {
    return "";
  }

  const corrected = row[correctedField];

  if (corrected && corrected !== "") {
    return (
      <span>
        <span style={{ textDecoration: "line-through" }}>
          {formatTime(row[field])}
        </span>{" "}
        <span>{formatTime(corrected)}</span>
      </span>
    );
  }

  return formatTime(row[field]);
}

function getSortableTime(row, field, correctedField) {
  if (row.tipoRegistro !== "Horario") {
    return "";
  }

  const corrected = row[correctedField];
  return corrected && corrected !== "" ? corrected : row[field];
}

function getTipoText(tipo) {
  return LABOR_CONTROL_TYPES.find((t) => t.id === tipo)?.text || "";
}

const ESTADO_ICONS = {
  Valido: "/images/check-blue.svg",
  Aviso: "/images/support-extra/aviso.svg",
  RevisionRegistro: "/images/support-extra/bandera.svg",
  RevisionCorreccion: "/images/support-extra/bandera.svg",
  Anulada: "/images/support-extra/cancelar.svg",
};

function getEstadoIcon(estado) {
  return ESTADO_ICONS[estado] || "";
}

const ESTADO_LABELS = {
  Valido: "Válido",
  Aviso: "Aviso",
  RevisionRegistro: "Revisión",
  RevisionCorreccion: "Revisión",
  Anulada: "Anulada",
};

function getEstadoTooltipContent(row) {
  const label = ESTADO_LABELS[row.estado] || "";

  if (row.estado === "Anulada" && row.motivoAnulacion) {
    return (
      <>
        {label}
        <br />
        {row.motivoAnulacion}
      </>
    );
  }

  return label;
}

function getHorasColumnText(row) {
  if (row.tipoRegistro === "Diario") {
    return row.dias;
  }

  if (row.estado === "Valido") {
    const minutos = getValidoIntervalMinutes(row);
    return `${Math.floor(minutos / 60)} horas y ${minutos % 60} minutos`;
  }

  return row.horas;
}

function getSortableMinutes(row) {
  const text = getHorasColumnText(row);

  if (!text || text === "Falta fin") {
    return 0;
  }

  if (row.tipoRegistro === "Diario") {
    const match = text.match(/(\d+)/);
    return match ? parseInt(match[1], 10) * 24 * 60 : 0;
  }

  const match = text.match(/(\d+)\s*horas?\s*y\s*(\d+)\s*minutos?/i);
  if (!match) {
    return 0;
  }

  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

function getMinutesFromTime(timeStr) {
  if (!timeStr) {
    return null;
  }

  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
}

function getValidoIntervalMinutes(row) {
  const useCorrected = row.inicioCorregido !== "" && row.finCorregido !== "";
  const inicio = useCorrected ? row.inicioCorregido : row.inicio;
  const fin = useCorrected ? row.finCorregido : row.fin;

  const inicioMinutos = getMinutesFromTime(inicio);
  const finMinutos = getMinutesFromTime(fin);

  if (inicioMinutos == null || finMinutos == null) {
    return 0;
  }

  return finMinutos - inicioMinutos;
}

function getTotalIntervalMinutes(row) {
  if (row.estado === "Valido") {
    return getValidoIntervalMinutes(row);
  }

  if (row.inicio === "" || row.fin === "") {
    return 0;
  }

  const inicioMinutos = getMinutesFromTime(row.inicio);
  const finMinutos = getMinutesFromTime(row.fin);

  if (inicioMinutos == null || finMinutos == null) {
    return 0;
  }

  return finMinutos - inicioMinutos;
}

export default function LaborControl({
  show,
  agentId,
  agentName,
  createPopupQuestion,
  removePopupQuestion,
}) {
  const [filterYear, setFilterYear] = useState("");
  const [filterAgent, setFilterAgent] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterMonths, setFilterMonths] = useState([]);
  const [filterTypes, setFilterTypes] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [fichado, setFichado] = useState(false);
  const [yearOptions, setYearOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentsFull, setAgentsFull] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [screenDataLoaded, setScreenDataLoaded] = useState(false);
  const [records, setRecords] = useState([]);
  const [startingJornada, setStartingJornada] = useState(false);
  const [endingJornada, setEndingJornada] = useState(false);
  const [hoursEditionData, setHoursEditionData] = useState(null);
  const [registerCancellationData, setRegisterCancellationData] =
    useState(null);
  const [showRegisterDeclaration, setShowRegisterDeclaration] =
    useState(false);
  const firstRecordsCallDoneRef = useRef(false);

  async function getScreenData() {
    const response = await post("ObtenerPantallaJornadaLaboral.php", {
      token: userData.token,
      idUsuario: agentId,
    });

    setIsAdmin(response.admin);
    setScreenDataLoaded(true);

    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = response.primerAno; y <= currentYear; y++) {
      years.push({ id: y, text: String(y) });
    }
    setYearOptions(years);
    setFilterYear(currentYear);

    setAgentsFull(response.agentes || []);
    setAgentOptions(
      (response.agentes || []).map((a) => ({
        id: a.id,
        text: (a.agente || "").toUpperCase(),
      })),
    );
    setFilterAgent(agentId);

    const currentMonth = MONTHS.find((m) => m.id === new Date().getMonth() + 1);
    if (currentMonth) {
      setFilterMonths([currentMonth.text]);
    }
  }

  useEffect(() => {
    if (show) {
      setFilterYear("");
      setFilterAgent("");
      setFilterCompany("");
      setFilterMonths([]);
      setFilterTypes([]);
      setFilterStatus([]);
      setSelectedRecords([]);
      setIsAdmin(false);
      setFichado(false);
      setYearOptions([]);
      setAgentOptions([]);
      setAgentsFull([]);
      setCompanyOptions([]);
      setScreenDataLoaded(false);
      setRecords([]);
      firstRecordsCallDoneRef.current = false;
      getScreenData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    const agent = agentsFull.find((a) => a.id === filterAgent);
    if (agent) {
      setCompanyOptions([
        { id: agent.idEmpresa, text: (agent.nombreEmpresa || "").toUpperCase() },
      ]);
      setFilterCompany(agent.idEmpresa);
      setFichado(agent.fichado);
    } else {
      setCompanyOptions([]);
      setFilterCompany("");
      setFichado(false);
    }
  }, [filterAgent, agentsFull]);

  async function getRecords() {
    const meses = filterMonths
      .map((text) => MONTHS.find((m) => m.text === text)?.id)
      .filter((id) => id != null);

    const response = await post("ListarRegistrosJornadas.php", {
      token: userData.token,
      idAgente: filterAgent,
      idEmpresa: filterCompany,
      ano: filterYear,
      meses,
    });

    const registrosHorarios = (response.registrosHorarios || []).map((r) => ({
      ...r,
      tipoRegistro: "Horario",
    }));
    const registrosDiarios = (response.registrosDiarios || []).map((r) => ({
      ...r,
      tipoRegistro: "Diario",
    }));

    const merged = [...registrosHorarios, ...registrosDiarios].sort((a, b) => {
      const dateDiff =
        new Date(getRecordDate(b)) - new Date(getRecordDate(a));
      if (dateDiff !== 0) {
        return dateDiff;
      }

      const startA = getSortableTime(a, "inicio", "inicioCorregido") || "";
      const startB = getSortableTime(b, "inicio", "inicioCorregido") || "";
      return startB.localeCompare(startA);
    });

    setRecords(merged);
  }

  async function iniciarJornada() {
    setStartingJornada(true);
    const response = await post("IniciarJornada.php", {
      token: userData.token,
      idAgente: filterAgent,
      idEmpresa: filterCompany,
    });
    setStartingJornada(false);

    setRecords((prev) => [{ ...response, tipoRegistro: "Horario" }, ...prev]);

    setAgentsFull((prev) =>
      prev.map((a) => (a.id === filterAgent ? { ...a, fichado: true } : a)),
    );
  }

  async function finalizarJornada() {
    setEndingJornada(true);
    const response = await post("FinalizarJornada.php", {
      token: userData.token,
      idAgente: filterAgent,
      idEmpresa: filterCompany,
    });
    setEndingJornada(false);

    setRecords((prev) =>
      prev.map((row) =>
        row.tipoRegistro === "Horario" &&
        String(row.id) === String(response.id)
          ? { ...response, tipoRegistro: "Horario" }
          : row,
      ),
    );

    setAgentsFull((prev) =>
      prev.map((a) => (a.id === filterAgent ? { ...a, fichado: false } : a)),
    );
  }

  useEffect(() => {
    if (!screenDataLoaded) {
      return;
    }

    if (firstRecordsCallDoneRef.current) {
      getRecords();
      return;
    }

    const allFilterFieldsFilled =
      filterYear !== "" &&
      filterAgent !== "" &&
      filterAgent != null &&
      filterCompany !== "" &&
      filterCompany != null &&
      filterMonths.length > 0;

    if (allFilterFieldsFilled) {
      firstRecordsCallDoneRef.current = true;
      getRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear, filterAgent, filterCompany, filterMonths, screenDataLoaded]);

  const selectedAgentName =
    agentOptions.find((a) => a.id === filterAgent)?.text || agentName || "";

  const handleCorregir = () => {
    if (selectedRecords.length >= 2) {
      createPopupQuestion(
        "Debe seleccionar un único registro para su corrección.",
        removePopupQuestion,
        null,
        "Entendido",
      );
      return;
    }

    if (selectedRecords.length === 1) {
      const row = records.find((r) => r.id === selectedRecords[0]);
      if (row) {
        setHoursEditionData({
          idRegistro: row.id,
          agente: selectedAgentName,
          inicioOriginal: formatTime(row.inicio),
          finOriginal: formatTime(row.fin),
        });
      }
    }
  };

  const handleAnular = () => {
    if (selectedRecords.length >= 2) {
      createPopupQuestion(
        "Debe seleccionar un único registro para su anulación.",
        removePopupQuestion,
        null,
        "Entendido",
      );
      return;
    }

    if (selectedRecords.length === 1) {
      const row = records.find((r) => r.id === selectedRecords[0]);
      if (row) {
        const useCorrected =
          row.tipoRegistro === "Horario" &&
          row.inicioCorregido !== "" &&
          row.finCorregido !== "";

        setRegisterCancellationData({
          idRegistro: row.id,
          fecha: formatFechaColumn(row),
          agente: row.agente,
          inicio:
            row.tipoRegistro === "Horario"
              ? formatTime(useCorrected ? row.inicioCorregido : row.inicio)
              : "",
          fin:
            row.tipoRegistro === "Horario"
              ? formatTime(useCorrected ? row.finCorregido : row.fin)
              : "",
          tipo: getTipoText(row.tipo),
          tipoRegistro: row.tipoRegistro,
        });
      }
    }
  };

  const selectedTipoIds = filterTypes
    .map((text) => LABOR_CONTROL_TYPES.find((t) => t.text === text)?.id)
    .filter(Boolean);

  const selectedEstadoIds = filterStatus
    .map((text) => LABOR_CONTROL_STATUS.find((s) => s.text === text)?.id)
    .filter(Boolean);

  const filteredRecords = records.filter((row) => {
    const tipoMatches =
      selectedTipoIds.length === 0 || selectedTipoIds.includes(row.tipo);

    const estadoMatches =
      selectedEstadoIds.length === 0 ||
      selectedEstadoIds.some((id) =>
        id === "Revision"
          ? row.estado === "RevisionRegistro" ||
            row.estado === "RevisionCorreccion"
          : row.estado === id,
      );

    return tipoMatches && estadoMatches;
  });

  let listadoTitle = "Listado de registros";
  if (filterMonths.length === 1) {
    listadoTitle = `Listado de registros de ${filterMonths[0]}`;
  } else if (filterMonths.length > 1) {
    listadoTitle = `Listado de registros de ${filterMonths.length} meses`;
  }

  const totalMinutos = filteredRecords
    .filter(
      (row) =>
        row.tipoRegistro === "Horario" &&
        (row.estado === "Valido" ||
          row.estado === "RevisionRegistro" ||
          row.estado === "RevisionCorreccion"),
    )
    .reduce((sum, row) => sum + getTotalIntervalMinutes(row), 0);

  if (totalMinutos > 0) {
    listadoTitle += `, total ${Math.floor(totalMinutos / 60)} horas y ${
      totalMinutos % 60
    } minutos`;
  }

  const selectedRows = records.filter((row) =>
    selectedRecords.includes(row.id),
  );

  const corregirEnabled = selectedRows.some(
    (row) =>
      row.tipoRegistro === "Horario" &&
      row.inicioCorregido === "" &&
      row.finCorregido === "" &&
      row.estado === "Valido",
  );

  const validarAnularEnabled = selectedRows.some(
    (row) =>
      row.estado === "RevisionCorreccion" || row.estado === "RevisionRegistro",
  );

  const handleValidar = () => {
    const rows = selectedRows.filter(
      (row) =>
        row.estado === "RevisionRegistro" ||
        row.estado === "RevisionCorreccion",
    );

    post("ValidarRegistrosJornadas.php", {
      token: userData.token,
      registros: rows.map((row) => ({
        id: row.id,
        tipo: row.tipoRegistro,
      })),
    });

    createPopupQuestion(
      `Se validaron ${rows.length} registros.`,
      () => {
        removePopupQuestion();
        setSelectedRecords([]);
        getRecords();
      },
      null,
      "Cerrar",
    );
  };

  const columns = [
    {
      name: "Fecha",
      selector: (row) => formatFechaColumn(row),
      sortable: true,
      width: "17%",
      sortFunction: (rowA, rowB) =>
        new Date(getRecordDate(rowA)) - new Date(getRecordDate(rowB)),
    },
    {
      name: "Agente",
      selector: (row) => row.agente,
      sortable: true,
      width: "27%",
    },
    {
      name: "Inicio",
      selector: (row) => formatTimeColumn(row, "inicio", "inicioCorregido"),
      sortable: true,
      width: "9%",
      center: true,
      sortFunction: (rowA, rowB) =>
        (getSortableTime(rowA, "inicio", "inicioCorregido") || "").localeCompare(
          getSortableTime(rowB, "inicio", "inicioCorregido") || "",
        ),
    },
    {
      name: "Fin",
      selector: (row) => formatTimeColumn(row, "fin", "finCorregido"),
      sortable: true,
      width: "9%",
      center: true,
      sortFunction: (rowA, rowB) =>
        (getSortableTime(rowA, "fin", "finCorregido") || "").localeCompare(
          getSortableTime(rowB, "fin", "finCorregido") || "",
        ),
    },
    {
      name: "Tipo",
      selector: (row) => getTipoText(row.tipo),
      sortable: true,
      width: "13%",
    },
    {
      name: "Estado",
      selector: (row) => {
        const icon = getEstadoIcon(row.estado);
        if (!icon) {
          return "";
        }

        const tooltipId = `labor-control-estado-${row.id}`;
        return (
          <>
            <img
              src={icon}
              className="icon-pref"
              alt="Estado"
              data-tooltip-id={tooltipId}
            ></img>
            <TooltipBox
              id={tooltipId}
              content={
                <div className="opt-table-tooltip grid-5">
                  <div className="cell combined-cells-5 cell-start-content">
                    {getEstadoTooltipContent(row)}
                  </div>
                </div>
              }
            />
          </>
        );
      },
      sortable: true,
      width: "6%",
      center: true,
      sortFunction: (rowA, rowB) =>
        (rowA.estado || "").localeCompare(rowB.estado || ""),
    },
    {
      name: "Horas",
      selector: (row) => (getHorasColumnText(row) || "").toUpperCase(),
      sortable: true,
      width: "14%",
      sortFunction: (rowA, rowB) =>
        getSortableMinutes(rowA) - getSortableMinutes(rowB),
    },
    {
      name: "",
      selector: (row) => (
        <CheckBox
          active={selectedRecords.includes(row.id)}
          onClick={() => handleCheckboxChange(row.id)}
        />
      ),
      sortable: false,
      width: "5%",
      center: true,
    },
  ];

  const handleCheckboxChange = (id) => {
    setSelectedRecords((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id],
    );
  };

  const resetFilters = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = MONTHS.find((m) => m.id === new Date().getMonth() + 1);

    setFilterYear(currentYear);
    setFilterAgent(agentId);
    setFilterMonths(currentMonth ? [currentMonth.text] : []);
    setFilterTypes([]);
    setFilterStatus([]);
  };

  if (!show) {
    return <></>;
  }

  return (
    <div className="patient-data">
      <div className="patient-data-header">
        <span className="header-text">Control laboral</span>
      </div>
      <div className="patient-data-content">
        <div className="action-bar">
          <div className="action-bar-resume">
            <span className="signed-text">Control laboral</span>
          </div>
        </div>
        <div className="patient-search-content">
          <div className="opt-table-title labor-control-title-bar">
            <span>
              Registro laboral
              {selectedAgentName ? ` de ${selectedAgentName}` : ""}
            </span>
            <button className="cell-label" onClick={resetFilters} tabIndex={-1}>
              Limpiar filtros
            </button>
          </div>
          <div
            className="opt-table-content grid-8"
            id="labor-control-filters-grid"
          >
            <div className="cell cell-start-content">Año</div>
            <div className="cell">
              <Dropdown
                id="labor-control-year"
                size={0}
                optionList={yearOptions}
                op={filterYear}
                onChange={(e) => setFilterYear(e.id)}
              />
            </div>
            <div className="cell"></div>
            <div className="cell cell-start-content">Agente</div>
            <div className="cell">
              <Dropdown
                id="labor-control-filter-agent"
                size={0}
                disabled={!isAdmin}
                optionList={agentOptions}
                op={filterAgent}
                onChange={(e) => setFilterAgent(e.id)}
              />
            </div>
            <div className="cell"></div>
            <div className="cell cell-start-content">Empresa</div>
            <div className="cell">
              <Dropdown
                id="labor-control-company"
                size={0}
                optionList={companyOptions}
                op={filterCompany}
                onChange={(e) => setFilterCompany(e.id)}
              />
            </div>
            <div className="cell cell-start-content">Mes</div>
            <div className="cell">
              <InputIconSearchPanelMultiSelection
                size={0}
                inactive={false}
                options={MONTHS.map((m) => m.text)}
                selectedOptions={filterMonths}
                onChange={setFilterMonths}
              />
            </div>
            <div className="cell"></div>
            <div className="cell cell-start-content">Tipo</div>
            <div className="cell">
              <InputIconSearchPanelMultiSelection
                size={0}
                inactive={false}
                options={LABOR_CONTROL_TYPES.map((t) => t.text)}
                selectedOptions={filterTypes}
                onChange={setFilterTypes}
              />
            </div>
            <div className="cell"></div>
            <div className="cell cell-start-content">Estado</div>
            <div className="cell">
              <InputIconSearchPanelMultiSelection
                size={0}
                inactive={false}
                options={LABOR_CONTROL_STATUS.map((s) => s.text)}
                selectedOptions={filterStatus}
                onChange={setFilterStatus}
              />
            </div>
          </div>
          {filterMonths.length > 0 && (
            <div className="labor-control-selected-tags">
              <span className="labor-control-selected-tags-title">
                Meses seleccionados: {filterMonths.length}
              </span>
              <div className="tag-content">
                {filterMonths.map((e, i) => {
                  return (
                    <Tag
                      text={e}
                      key={i}
                      tagId={i}
                      onClick={(e) => {
                        setFilterMonths((prev) =>
                          prev.filter((month) => month !== e),
                        );
                      }}
                    ></Tag>
                  );
                })}
              </div>
            </div>
          )}
          {filterTypes.length > 0 && (
            <div className="labor-control-selected-tags">
              <span className="labor-control-selected-tags-title">
                Tipos seleccionados: {filterTypes.length}
              </span>
              <div className="tag-content">
                {filterTypes.map((e, i) => {
                  return (
                    <Tag
                      text={e}
                      key={i}
                      tagId={i}
                      onClick={(e) => {
                        setFilterTypes((prev) =>
                          prev.filter((type) => type !== e),
                        );
                      }}
                    ></Tag>
                  );
                })}
              </div>
            </div>
          )}
          {filterStatus.length > 0 && (
            <div className="labor-control-selected-tags">
              <span className="labor-control-selected-tags-title">
                Estados seleccionados: {filterStatus.length}
              </span>
              <div className="tag-content">
                {filterStatus.map((e, i) => {
                  return (
                    <Tag
                      text={e}
                      key={i}
                      tagId={i}
                      onClick={(e) => {
                        setFilterStatus((prev) =>
                          prev.filter((status) => status !== e),
                        );
                      }}
                    ></Tag>
                  );
                })}
              </div>
            </div>
          )}
          <div className="labor-control-registro-actions">
            <ButtonIconH
              text="Iniciar jornada"
              disabled={!screenDataLoaded || fichado || startingJornada}
              onClick={iniciarJornada}
            ></ButtonIconH>
            <ButtonIconH
              text="Finalizar jornada"
              disabled={!screenDataLoaded || !fichado || endingJornada}
              onClick={finalizarJornada}
            ></ButtonIconH>
            <ButtonIconH
              text="Declarar registro"
              onClick={() => setShowRegisterDeclaration(true)}
            ></ButtonIconH>
          </div>
        </div>
        <div className="patient-search-result-content labor-control-results-content">
          <div className="psr-header">
            <div className="ps-title">
              <div className="title-h2">{listadoTitle}</div>
            </div>
          </div>
          <div className="breaker-gr mb-8"></div>
          <div className="labor-control-table-actions">
            {isAdmin && (
              <>
                <ButtonIconH
                  text="Validar"
                  small={true}
                  disabled={!validarAnularEnabled}
                  onClick={handleValidar}
                ></ButtonIconH>
                <ButtonIconH
                  text="Anular"
                  small={true}
                  disabled={!validarAnularEnabled}
                  onClick={handleAnular}
                ></ButtonIconH>
              </>
            )}
            <ButtonIconH
              text="Corregir"
              small={true}
              disabled={!corregirEnabled}
              onClick={handleCorregir}
            ></ButtonIconH>
          </div>
          <DataTable
            columns={columns}
            data={filteredRecords}
            className="custom-datatable"
            noDataComponent="No hay registros disponibles."
          />
        </div>
      </div>
      <PopupHoursEdition
        show={!!hoursEditionData}
        idRegistro={hoursEditionData?.idRegistro}
        agente={hoursEditionData?.agente}
        inicioOriginal={hoursEditionData?.inicioOriginal}
        finOriginal={hoursEditionData?.finOriginal}
        onCancel={() => setHoursEditionData(null)}
        onAccept={() => {
          setSelectedRecords([]);
          getRecords();
        }}
        createPopupQuestion={createPopupQuestion}
        removePopupQuestion={removePopupQuestion}
      />
      <PopupRegisterCancellation
        show={!!registerCancellationData}
        idRegistro={registerCancellationData?.idRegistro}
        fecha={registerCancellationData?.fecha}
        agente={registerCancellationData?.agente}
        inicio={registerCancellationData?.inicio}
        fin={registerCancellationData?.fin}
        tipo={registerCancellationData?.tipo}
        tipoRegistro={registerCancellationData?.tipoRegistro}
        onCancel={() => setRegisterCancellationData(null)}
        onAccept={() => {
          setSelectedRecords([]);
          getRecords();
        }}
        createPopupQuestion={createPopupQuestion}
        removePopupQuestion={removePopupQuestion}
      />
      <PopupRegisterDeclaration
        show={showRegisterDeclaration}
        agente={selectedAgentName}
        idAgente={filterAgent}
        idEmpresa={filterCompany}
        onCancel={() => setShowRegisterDeclaration(false)}
        onAccept={() => {
          setSelectedRecords([]);
          getRecords();
        }}
        createPopupQuestion={createPopupQuestion}
        removePopupQuestion={removePopupQuestion}
      />
    </div>
  );
}
